import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  Flex,
  Alert,
  Avatar,
  Tag,
  Spin,
  App as AntApp,
} from 'antd';
import {
  GoogleOutlined,
  UserOutlined,
  LogoutOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { User } from '@supabase/supabase-js';
import {
  getCurrentUser,
  signInWithGoogle,
  onAuthStateChange,
  isDevAuthMock,
} from '../auth/index.js';
import { startPageSync } from '../services/syncEngine.js';
import { confirmAndSignOut } from './confirmSignOut.js';
import { antThemeConfig } from '../theme/index.js';
import { friendlyAuthError } from './authErrors.js';
import { Echo } from './Echo.js';
import { platform } from '../platform.js';

const { Title, Text, Paragraph } = Typography;

interface AuthGateProps {
  children: React.ReactNode;
  /** Whether to render the persistent user profile badge at the top when authenticated */
  showUserBar?: boolean;
  /** Custom title for the current view (e.g., "Review Session", "Control Panel") */
  viewTitle?: string;
  /** Optional container style override */
  containerStyle?: React.CSSProperties;
}

export const AuthGate: React.FC<AuthGateProps> = ({
  children,
  showUserBar = true,
  viewTitle,
  containerStyle,
}) => {
  const { message, modal } = AntApp.useApp();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const googleButtonRef = useRef<HTMLButtonElement | null>(null);

  // Initial user check and auth listener setup
  useEffect(() => {
    let isMounted = true;

    async function checkAuth() {
      try {
        const currentUser = await getCurrentUser();
        if (isMounted) {
          setUser(currentUser);
        }
      } catch (err) {
        console.warn('[LingoPing AuthGate] Error checking auth:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    checkAuth();

    const { data: authListener } = onAuthStateChange((_event, session) => {
      // The simulated development sign-in never has a real session, so ignore "no session" events.
      if (isMounted && !(isDevAuthMock() && !session)) {
        setUser(session?.user || null);
      }
    });

    return () => {
      isMounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // While a learner is signed in on this page it keeps in step with their account.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    let stop: (() => void) | undefined;
    let unmounted = false;
    // First make sure this device has noted who is signed in (after the redirect back from
    // Google the session can be here before that): the sync works for the noted learner.
    void getCurrentUser().then(() => {
      if (!unmounted) stop = startPageSync();
    });
    return () => {
      unmounted = true;
      stop?.();
    };
  }, [userId]);

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      setAuthError(null);
      const res = await signInWithGoogle();
      if (res.success && res.user) {
        setUser(res.user);
        if (!isDevAuthMock()) {
          message.success(
            `Welcome to LingoPing, ${res.user.user_metadata?.full_name || res.user.email}!`
          );
        }
      } else if (!res.redirecting) {
        console.warn('[LingoPing AuthGate] Google sign-in failed:', res.error);
        setAuthError(friendlyAuthError(res.error));
      }
    } catch (err: any) {
      console.warn('[LingoPing AuthGate] Google sign-in error:', err);
      setAuthError(friendlyAuthError(err?.message));
    } finally {
      setGoogleLoading(false);
    }
  };

  // After a failed attempt, put the focus back on the button so a keyboard user can simply retry.
  useEffect(() => {
    if (authError) googleButtonRef.current?.focus();
  }, [authError]);

  const handleSignOut = async () => {
    try {
      if (!(await confirmAndSignOut(modal))) return;
      setUser(null);
      message.info("You've been signed out.");
    } catch {
      message.error("Couldn't sign you out. Please try again.");
    }
  };

  // Loading state
  if (loading) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{
          minHeight: 380,
          padding: 24,
          flexDirection: 'column',
          gap: 16,
          ...containerStyle,
        }}
      >
        <Spin size="large" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Loading…
        </Text>
      </Flex>
    );
  }

  // Authenticated: Render children with optional top profile bar
  if (user) {
    return (
      <div style={containerStyle}>
        {showUserBar && (
          <Flex
            justify="space-between"
            align="center"
            style={{
              padding: '6px 12px',
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              marginBottom: 10,
              borderRadius: 6,
            }}
          >
            <Space size={8} align="center">
              <Avatar
                size="small"
                src={user.user_metadata?.avatar_url}
                icon={<UserOutlined />}
                style={{ backgroundColor: '#1677ff' }}
              />
              <Flex vertical style={{ lineHeight: 1.2 }}>
                <Text
                  ellipsis
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    maxWidth: 190,
                    color: '#0f172a',
                  }}
                >
                  {user.user_metadata?.full_name || user.email}
                </Text>
                {(viewTitle || (user.user_metadata?.full_name && user.email)) && (
                  <Text type="secondary" ellipsis style={{ fontSize: 11, maxWidth: 190 }}>
                    {viewTitle || user.email}
                  </Text>
                )}
              </Flex>
            </Space>

            <Button
              type="text"
              size="small"
              icon={<LogoutOutlined />}
              onClick={handleSignOut}
              style={{ color: '#475569', fontSize: 12 }}
            >
              Sign out
            </Button>
          </Flex>
        )}
        {children}
      </div>
    );
  }

  // Unauthenticated: Render Onboarding & Authentication Gate
  return (
    <div
      style={{
        padding: '20px 16px',
        boxSizing: 'border-box',
        maxWidth: 420,
        margin: '0 auto',
        fontFamily: antThemeConfig.token?.fontFamily,
        overflowY: 'auto',
        ...containerStyle,
      }}
    >
      {/* Branded Logo & Header */}
      <Flex vertical align="center" style={{ textAlign: 'center', marginBottom: 20 }}>
        <Echo mood="hello" size={112} style={{ marginBottom: 6 }} />

        <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
          LingoPing
        </Title>
        <Text style={{ fontSize: 13, marginTop: 4, color: '#475569' }}>
          Learn German words, a few minutes at a time.
        </Text>
      </Flex>

      {/* Main Authentication Card */}
      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
        styles={{ body: { padding: '16px 14px' } }}
      >
        <Paragraph
          style={{ fontSize: 13, textAlign: 'center', color: '#334155', marginBottom: 14 }}
        >
          Sign in to start learning.
        </Paragraph>

        {authError && (
          <Alert
            title={authError}
            type="error"
            showIcon
            closable={{ onClose: () => setAuthError(null) }}
            style={{ marginBottom: 14, fontSize: 13 }}
          />
        )}

        {isDevAuthMock() && (
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <Tag
              color="processing"
              icon={<ThunderboltOutlined />}
              style={{
                borderRadius: 12,
                padding: '2px 10px',
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              Development build: simulated sign-in
            </Tag>
          </div>
        )}

        {/* Primary Action: Continue with Google */}
        <Button
          ref={googleButtonRef}
          type="primary"
          block
          size="large"
          icon={<GoogleOutlined style={{ fontSize: 16 }} />}
          loading={googleLoading}
          onClick={handleGoogleSignIn}
          style={{
            height: 44,
            fontWeight: 600,
            backgroundColor: '#2563eb',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
          }}
        >
          Continue with Google
        </Button>

        <Paragraph
          type="secondary"
          style={{ fontSize: 12, textAlign: 'center', margin: '12px 0 0' }}
        >
          No password to remember.
        </Paragraph>
      </Card>
      {platform.SignInFooter && (
        <div style={{ marginTop: 20 }}>
          <platform.SignInFooter />
        </div>
      )}
    </div>
  );
};
